import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Copy,
  History as HistoryIcon,
  MoreHorizontal,
  Pencil,
  Plus,
  Printer,
  Trash2,
  TriangleAlert,
} from "lucide-react";
import { useApi } from "../api";
import { useCurrentUser } from "../auth";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  Input,
  Textarea,
  Alert,
  Status,
  Loading,
  Empty,
  ConfirmDialog,
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui";
import { StatementXmlEditor } from "../components/statement-nodes/StatementXmlEditor";
import {
  analyzeStatementXmlSegments,
  serializeStatementXmlSegments,
  statementXmlToPlainText,
} from "../components/statement-nodes/parse";
import {
  createEmptySentenceRow,
  sentenceRowsToSegments,
} from "../components/statement-nodes/rows";
import { BASE_LANGUAGE_OPTIONS } from "../constants/languages";
import { PRINTER_LIST_PERMISSION } from "../constants/permissions";
import { extractApiErrorMessage } from "../apiErrors";
import { StatementRead, Pictograms } from "../work/StatementRead";
import {
  HistoryPanel,
  PictogramsCard,
  ProductDetailsScope,
  ProductHeader,
  SourceFilePanel,
  WebsitePanel,
} from "../work/ProductPanels";
import { useLocalDraft } from "../work/useLocalDraft";
import {
  type ProductRecord,
  type LanguageVersion,
  type WebsiteRecord,
  languageName,
  languageStatus,
  generationEligible,
} from "../work/types";
import "../work/work.css";

type Extracted = {
  text: string;
  fileId: string;
  extractionId: string;
  pictograms: string[];
};
function LanguageEditor({
  product,
  version,
  source,
  website,
  onChanged,
  refresh,
  setRefresh,
  injected,
  onInjectionConsumed,
  startEditing = false,
  status,
  historyButton,
}: {
  onInjectionConsumed: () => void;
  startEditing?: boolean;
  status: React.ReactNode;
  historyButton: React.ReactNode;
  product: ProductRecord;
  version: LanguageVersion;
  source?: LanguageVersion;
  website: boolean;
  onChanged: () => Promise<void>;
  refresh: boolean;
  setRefresh: (value: boolean) => void;
  injected: Extracted | null;
}) {
  const api = useApi();
  const { t, i18n } = useTranslation("work");
  const { permissions } = useCurrentUser();
  const initial = version.latest_text || "";
  const draft = useLocalDraft(
    `product:${product.id}:${version.language}`,
    { text: initial, sds: null as Extracted | null },
    version.version_id || String(version.latest_version),
  );
  const [editing, setEditing] = useState(!initial || startEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [ack, setAck] = useState(false);
  const [balance, setBalance] = useState<number | null>(null);
  const request = useRef<{ payload: string; key: string } | null>(null);
  const isBase = version.language === product.base_language;
  const canEdit = permissions.includes("translation.edit");
  const languages = (product.language_states || []).filter(
    (v) => v.language !== product.base_language,
  );
  const manual = languages.filter(
    (v) => v.has_manual_changes || v.status === "needs_attention",
  );
  const parsed = analyzeStatementXmlSegments(draft.value.text);
  const segments = parsed.hasNodes
    ? parsed.segments
    : sentenceRowsToSegments(
        draft.value.text
          ? [{ ...createEmptySentenceRow(), text: draft.value.text }]
          : [createEmptySentenceRow()],
      );
  const unsupported = !parsed.hasNodes && /<[!?/A-Za-z]/.test(draft.value.text);
  const unresolved = segments.some(
    (s) =>
      s.kind === "node" &&
      ["unresolved", "ambiguous"].includes(s.node.attributes.matchStatus || ""),
  );
  const draftNeedsAttention = unresolved || unsupported;
  const referenceBlocked =
    !!version.reference_issues?.length ||
    languages.some((v) => !!v.reference_issues?.length);
  const refreshActive = isBase && refresh && !draft.value.sds;
  const refreshBlocked =
    refreshActive &&
    (referenceBlocked ||
      (manual.length > 0 && !ack) ||
      (balance !== null && balance < languages.length));
  useEffect(() => {
    if (draft.recovered) setEditing(true);
  }, [draft.recovered]);
  useEffect(() => {
    if (injected) {
      draft.setValue({ text: injected.text, sds: injected });
      setEditing(true);
      setError("");
      onInjectionConsumed();
    }
  }, [injected]);
  useEffect(() => {
    if (permissions.includes("stats.usage"))
      api
        .get("/stats/actions-usage")
        .then((x) => setBalance(x.TRANSLATION_REQUEST?.available ?? null))
        .catch(() => {});
  }, [api, permissions]);
  const save = async () => {
    if (saving || unresolved || unsupported || refreshBlocked) return;
    setSaving(true);
    setError("");
    setMessage("");
    const xml = serializeStatementXmlSegments(segments);
    const payload = {
      text: xml,
      regenerate_existing_translations: refreshActive,
    };
    const serialized = JSON.stringify({ payload, sds: draft.value.sds });
    if (request.current?.payload !== serialized)
      request.current = { payload: serialized, key: crypto.randomUUID() };
    try {
      let response;
      if (draft.value.sds) {
        const sds = draft.value.sds;
        await api.put(`/products/${product.id}/sds`, {
          sds_file_id: sds.fileId,
          sds_clp_extraction_id: sds.extractionId,
          base_language_clp_text_override: xml,
          ghs_pictograms: sds.pictograms,
        });
        response = {};
      } else
        response = await api.put(
          `/products/${product.id}/translations/${version.language}`,
          payload,
          { headers: { "Idempotency-Key": request.current.key } },
        );
      draft.reset(
        { text: xml, sds: null },
        response.version?.id ||
          version.version_id ||
          String(version.latest_version),
      );
      request.current = null;
      setEditing(false);
      setMessage(t(website ? "savedWebsite" : "saved"));
      const auto = response.auto_translation;
      if (auto?.failed_languages?.length)
        setMessage(
          `${t("saved")} ${t("refreshFailed", { languages: auto.failed_languages.map((x: any) => languageName(x.language, i18n.language)).join(", ") })}`,
        );
      else if (auto?.succeeded_languages?.length)
        setMessage(
          `${t("saved")} ${t("refreshDone", { languages: auto.succeeded_languages.map((x: string) => languageName(x, i18n.language)).join(", ") })}`,
        );
      try {
        await onChanged();
      } catch {
        setError(t("savedRefreshFailed"));
      }
    } catch (e) {
      setError(
        `${t("saveFailed")} ${extractApiErrorMessage(e) || t("retryHelp")}`,
      );
    } finally {
      setSaving(false);
    }
  };
  const notices = (error ||
    message ||
    draft.recovered ||
    draft.stale ||
    draft.storageError ||
    !!version.reference_issues?.length) && (
    <div className="language-notices">
      {error && <Alert variant="error">{error}</Alert>}
      {message && <Alert variant="success">{message}</Alert>}
      {(draft.recovered || draft.stale) && (
        <Alert variant="info">
          {draft.recovered && t("draft.recovered")}
          {draft.stale && ` ${t("draft.stale")}`}
        </Alert>
      )}
      {draft.stale && (
        <details>
          <summary>{t("draft.compareLatest")}</summary>
          <StatementRead text={initial} language={version.language} />
        </details>
      )}
      {draft.storageError && (
        <Alert variant="warning">{t("draft.storageError")}</Alert>
      )}
      {version.reference_issues?.length ? (
        <Alert variant="warning">{t("referenceConflict")}</Alert>
      ) : null}
    </div>
  );
  if (!editing)
    return (
      <>
        <div className="language-toolbar">
          {status}
          <div className="work-actions">
            {historyButton}
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                void navigator.clipboard
                  .writeText(statementXmlToPlainText(initial))
                  .then(() => setMessage(t("copied")))
                  .catch(() => setError(t("copyFailed")))
              }
            >
              <Copy aria-hidden="true" />
              {t("copyText")}
            </Button>
            {canEdit && (
              <Button
                size="sm"
                onClick={() => {
                  setEditing(true);
                  setMessage("");
                }}
              >
                <Pencil aria-hidden="true" />
                {t("editStatements")}
              </Button>
            )}
          </div>
        </div>
        {notices}
        <StatementRead
          text={initial}
          language={version.language}
          source={!isBase ? source?.latest_text : undefined}
          issues={version.reference_issues}
        />
      </>
    );
  return (
    <>
      <div className="language-toolbar language-toolbar-sticky">
        <span className="language-state">
          <Status tone={draftNeedsAttention ? "warning" : "neutral"}>
            {t(draftNeedsAttention ? "draft.needsAttention" : "draft.editing")}
          </Status>
          <span className="work-muted">
            {t(draft.dirty ? "draft.unsaved" : "draft.unchanged")} ·{" "}
            {t(website ? "websiteTiming" : "staysOffWebsite")}
          </span>
        </span>
        <div className="work-actions">
          <Button
            variant="ghost"
            size="sm"
            disabled={saving}
            onClick={() => {
              draft.reset({ text: initial, sds: null });
              setEditing(false);
              setError("");
            }}
          >
            {t("discard")}
          </Button>
          <Button
            size="sm"
            disabled={
              !canEdit ||
              saving ||
              unresolved ||
              unsupported ||
              refreshBlocked ||
              !statementXmlToPlainText(draft.value.text).trim()
            }
            onClick={() => void save()}
          >
            {saving ? t("saving") : t("save")}
          </Button>
        </div>
      </div>
      {notices}
      {unresolved && (
        <div className="language-notices">
          <Alert variant="warning">{t("resolveBeforeSave")}</Alert>
        </div>
      )}
      {isBase &&
        languages.length > 0 &&
        permissions.includes("translation.translate") &&
        !draft.value.sds && (
          <div className="save-scope">
            <label>
              <Checkbox
                checked={refresh}
                onCheckedChange={(nextChecked) => {
                  setRefresh(nextChecked === true);
                  setAck(false);
                }}
                disabled={saving || (referenceBlocked && !refresh)}
              />
              <span>
                {t("refreshChoice", {
                  languages: languages
                    .map((x) => languageName(x.language, i18n.language))
                    .join(", "),
                  count: languages.length,
                })}
              </span>
            </label>
            {referenceBlocked && (
              <p className="inline-warning">{t("refreshReferenceBlocked")}</p>
            )}
            {refresh && (
              <>
                <p>
                  {t("refreshEffect")}{" "}
                  {balance !== null &&
                    t("creditsAvailable", { count: balance })}
                </p>
                {manual.length > 0 && (
                  <label>
                    <Checkbox
                      checked={ack}
                      onCheckedChange={(nextChecked) =>
                        setAck(nextChecked === true)
                      }
                      disabled={saving}
                    />
                    <span>
                      {t("manualAcknowledge", {
                        languages: manual
                          .map((x) => languageName(x.language, i18n.language))
                          .join(", "),
                      })}
                    </span>
                  </label>
                )}
              </>
            )}
          </div>
        )}
      {draft.value.sds && (
        <section className="language-sds-review">
          <Alert variant="warning">{t("sds.saveEffect")}</Alert>
          <h3>{t("pictograms")}</h3>
          <p className="work-muted">{t("sds.pictogramsReview")}</p>
          <Pictograms
            selected={draft.value.sds.pictograms}
            onChange={
              saving
                ? undefined
                : (pictograms) =>
                    draft.setValue({
                      ...draft.value,
                      sds: { ...draft.value.sds!, pictograms },
                    })
            }
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              draft.setValue({
                ...draft.value,
                sds: {
                  ...draft.value.sds!,
                  pictograms: product.ghs_pictograms || [],
                },
              })
            }
          >
            {t("sds.keepCurrentPictograms")}
          </Button>
        </section>
      )}
      {unsupported ? (
        <div className="language-notices">
          <Alert variant="error">{t("unsupportedXml")}</Alert>
          <Textarea
            aria-label={t("originalContent")}
            value={draft.value.text}
            disabled={saving || !canEdit}
            onChange={(event) =>
              draft.setValue({ ...draft.value, text: event.target.value })
            }
          />
        </div>
      ) : (
        <StatementXmlEditor
          segments={segments}
          onSegmentsChange={(next) =>
            draft.setValue({
              ...draft.value,
              text: serializeStatementXmlSegments(next),
            })
          }
          language={version.language}
          disabled={saving || !canEdit}
          disableClpAssignment={!isBase}
          referenceIssues={version.reference_issues?.map((issue) => ({
            row_index: issue.row_index ?? null,
            node_id: issue.node_id,
            code: issue.code,
            reason: issue.reason || "invalid_reference",
          }))}
          referenceSegments={
            !isBase && source?.latest_text
              ? analyzeStatementXmlSegments(source.latest_text).segments
              : null
          }
        />
      )}
    </>
  );
}

export default function ProductDetails() {
  const { id = "" } = useParams();
  const api = useApi();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("work");
  const { permissions, impersonatedOrgId, currentUser } = useCurrentUser();
  const [params, setParams] = useSearchParams();
  const [product, setProduct] = useState<ProductRecord | null>(null);
  const [versions, setVersions] = useState<LanguageVersion[]>([]);
  const [website, setWebsite] = useState<WebsiteRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [balance, setBalance] = useState<number | null>(null);
  const [history, setHistory] = useState(false);
  const [extracted, setExtracted] = useState<Extracted | null>(null);
  const [injected, setInjected] = useState<Extracted | null>(null);
  const [message, setMessage] = useState("");
  const loadSequence = useRef(0);
  const genRequest = useRef<{ payload: string; key: string } | null>(null);
  const canTranslate = permissions.includes("translation.translate");
  const canWebsite = permissions.includes("product.embed_manage");
  const canPrint = permissions.includes(PRINTER_LIST_PERMISSION);
  const load = useCallback(async () => {
    const sequence = ++loadSequence.current;
    const [p, v, w] = await Promise.all([
      api.get(`/products/${id}`),
      api.get(`/products/${id}/translations?includeText=true`),
      canWebsite ? api.get(`/products/${id}/embed`) : null,
    ]);
    if (sequence !== loadSequence.current) return;
    setProduct({
      ...p,
      translation_languages: v.map((x: LanguageVersion) => x.language),
      language_states: v,
    });
    setVersions(v);
    setWebsite(w);
  }, [api, id, canWebsite]);
  useEffect(() => {
    let live = true;
    setLoading(true);
    setProduct(null);
    setError("");
    load()
      .catch(() => {
        if (live) setError(t("productLoadError"));
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
      loadSequence.current++;
    };
  }, [load, t]);
  useEffect(() => {
    if (permissions.includes("stats.usage"))
      api
        .get("/stats/actions-usage")
        .then((x) => setBalance(x.TRANSLATION_REQUEST?.available ?? null))
        .catch(() => {});
  }, [api, permissions]);
  const selected = params.get("language") || product?.base_language || "en";
  const version = versions.find((v) => v.language === selected);
  const source = versions.find((v) => v.language === product?.base_language);
  const status = languageStatus(
    version,
    !!statementXmlToPlainText(source?.latest_text || "").trim(),
  );
  const onWebsite = website?.enabled ?? product?.website_enabled ?? false;
  const select = (language: string) => {
    setParams({ language });
    setHistory(false);
    setMessage("");
    setError("");
    setInjected(null);
  };
  const generate = async () => {
    if (!product || !source?.latest_text || busy) return;
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const current: LanguageVersion[] = await api.get(
        `/products/${id}/translations?includeText=true`,
      );
      const fresh = current.find((v) => v.language === product.base_language);
      if (
        fresh?.version_id !== source.version_id ||
        fresh?.latest_text !== source.latest_text ||
        current.some((v) => v.language === selected)
      ) {
        await load();
        setError(t("sourceChanged"));
        return;
      }
      const payload = {
        product: { id: product.id },
        source_language: product.base_language,
        target_language: selected,
        source_text: fresh.latest_text,
      };
      const serial = JSON.stringify(payload);
      if (genRequest.current?.payload !== serial)
        genRequest.current = { payload: serial, key: crypto.randomUUID() };
      await api.post("/translations/generate-and-save", payload, {
        headers: { "Idempotency-Key": genRequest.current.key },
      });
      setMessage(t(onWebsite ? "languageSavedWebsite" : "languageSaved"));
      if (balance !== null) setBalance(balance - 1);
      // Once the mutation succeeded, a failed read is not a failed generation.
      try {
        await load();
        genRequest.current = null;
      } catch {
        setError(t("savedRefreshFailed"));
      }
    } catch (e) {
      setError(
        `${t("generationFailed")} ${extractApiErrorMessage(e) || t("retryHelp")}`,
      );
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    setBusy(true);
    try {
      await api.del(`/products/${id}`);
      navigate("/products");
    } catch {
      setError(t("actionError"));
      setDeleteOpen(false);
    } finally {
      setBusy(false);
    }
  };
  if (loading) return <Loading label={t("loading")} />;
  if (!product)
    return (
      <Empty title={t("productLoadError")} description={error}>
        <Button
          variant="outline"
          onClick={() =>
            void load().catch(() => setError(t("productLoadError")))
          }
        >
          {t("retry")}
        </Button>
        <Button asChild variant="ghost">
          <Link to="/products">{t("backProducts")}</Link>
        </Button>
      </Empty>
    );
  const displayVersion = version || {
    head_id: "",
    language: selected,
    latest_version: 0,
    updated_at: "",
    latest_text: "",
  };
  const hasSource = !!statementXmlToPlainText(source?.latest_text || "").trim();
  const savedLanguages = [
    product.base_language,
    ...versions
      .map((v) => v.language)
      .filter((l) => l !== product.base_language),
  ];
  const tabLanguages = savedLanguages.includes(selected)
    ? savedLanguages
    : [...savedLanguages, selected];
  const statusBadge = (
    <span className="language-state">
      <Status
        tone={
          status === "up_to_date"
            ? "success"
            : status === "not_added"
              ? "neutral"
              : "warning"
        }
      >
        {t(`status.${status}`)}
      </Status>
      {version?.has_manual_changes && (
        <span className="work-muted">{t("manualChanges")}</span>
      )}
    </span>
  );
  const historyButton = version &&
    permissions.includes("translation.history") && (
      <Button
        variant="ghost"
        size="sm"
        aria-expanded={history}
        onClick={() => setHistory(!history)}
      >
        <HistoryIcon aria-hidden="true" />
        {t("history.title")}
      </Button>
    );
  return (
    <ProductDetailsScope key={product.id} product={product} onChanged={load}>
      {(details) => (
        <div className="work-page">
          <ProductHeader product={product} details={details}>
            {canPrint && (
              <Button asChild variant="outline">
                <Link
                  to={`/printing?query=${encodeURIComponent(product.ean || product.sku || product.name)}`}
                >
                  <Printer aria-hidden="true" />
                  {t("printLabel")}
                </Link>
              </Button>
            )}
            {permissions.includes("product.delete") && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="product-more"
                    aria-label={t("moreActions")}
                    title={t("moreActions")}
                  >
                    <MoreHorizontal aria-hidden="true" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="vl-menu">
                  <PopoverClose asChild>
                    <button
                      type="button"
                      className="vl-menu-item"
                      data-destructive
                      onClick={() => setDeleteOpen(true)}
                    >
                      <Trash2 aria-hidden="true" />
                      {t("deleteProduct")}
                    </button>
                  </PopoverClose>
                </PopoverContent>
              </Popover>
            )}
          </ProductHeader>
          {error && <Alert variant="error">{error}</Alert>}
          {message && <Alert variant="success">{message}</Alert>}
          {params.get("activation") === "failed" && (
            <Alert variant="warning">{t("activationFailed")}</Alert>
          )}
          <div className="product-workspace">
            <Card
              className="language-card"
              aria-label={languageName(selected, i18n.language)}
            >
              <nav className="vl-tabs language-tabs" aria-label={t("languages")}>
                {tabLanguages.map((code) => {
                  const state = languageStatus(
                    versions.find((v) => v.language === code),
                    hasSource,
                  );
                  return (
                    <button
                      type="button"
                      className="vl-tab"
                      key={code}
                      aria-current={selected === code ? "true" : undefined}
                      title={t(`status.${state}`)}
                      onClick={() => select(code)}
                    >
                      {languageName(code, i18n.language)}
                      {code === product.base_language && (
                        <span className="language-tab-note">
                          {t("sourceShort")}
                        </span>
                      )}
                      {["outdated", "needs_attention"].includes(state) && (
                        <>
                          <TriangleAlert
                            className="language-tab-warning"
                            aria-hidden="true"
                          />
                          <span className="sr-only">
                            {t(`status.${state}`)}
                          </span>
                        </>
                      )}
                    </button>
                  );
                })}
                {canTranslate && (
                  <LanguagePicker
                    startOpen={params.get("add") === "1"}
                    languages={BASE_LANGUAGE_OPTIONS.map((l) => l.code).filter(
                      (code) => !savedLanguages.includes(code),
                    )}
                    onSelect={select}
                  />
                )}
              </nav>
              {extracted && selected === product.base_language && (
                <div className="language-notices">
                  <Alert variant="warning">
                    <p>{t("sds.compareWarning")}</p>
                    <div className="work-actions">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setInjected(extracted);
                          setExtracted(null);
                        }}
                      >
                        {t("sds.useExtracted")}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setExtracted(null)}
                      >
                        {t("discard")}
                      </Button>
                    </div>
                  </Alert>
                </div>
              )}
              {history && version ? (
                <>
                  <div className="language-toolbar">
                    {statusBadge}
                    <div className="work-actions">{historyButton}</div>
                  </div>
                  <div className="language-history">
                    <HistoryPanel
                      key={version.head_id}
                      version={version}
                      onChanged={load}
                    />
                  </div>
                </>
              ) : version || selected === product.base_language ? (
                <LanguageEditor
                  key={`${impersonatedOrgId || currentUser?.org_id}:${id}:${selected}`}
                  product={product}
                  version={displayVersion}
                  source={source}
                  website={onWebsite}
                  onChanged={load}
                  refresh={refresh}
                  setRefresh={setRefresh}
                  injected={injected}
                  onInjectionConsumed={() => setInjected(null)}
                  startEditing={params.get("edit") === "1"}
                  status={statusBadge}
                  historyButton={historyButton}
                />
              ) : hasSource ? (
                <>
                  <div className="language-add-cta">
                    <div>
                      <h3>
                        {t("languageNotAdded", {
                          language: languageName(selected, i18n.language),
                        })}
                      </h3>
                      <p className="work-muted">
                        {t("addLanguageEffect", {
                          source: languageName(
                            product.base_language,
                            i18n.language,
                          ),
                        })}{" "}
                        {t(onWebsite ? "websiteTiming" : "staysOffWebsite")}{" "}
                        {balance !== null
                          ? t("creditsAvailable", { count: balance })
                          : t("costOne")}
                      </p>
                    </div>
                    <Button
                      disabled={
                        !canTranslate ||
                        busy ||
                        (balance !== null && balance < 1) ||
                        !generationEligible(product, selected)
                      }
                      onClick={() => void generate()}
                    >
                      {busy ? t("translating") : t("translateAndSave")}
                    </Button>
                  </div>
                  {!generationEligible(product, selected) && (
                    <div className="language-notices">
                      <Alert variant="warning">{t("correctSourceFirst")}</Alert>
                    </div>
                  )}
                  <h3 className="language-source-title">
                    {t("sourceStatements")}
                  </h3>
                  <StatementRead
                    text={source?.latest_text || ""}
                    language={product.base_language}
                  />
                </>
              ) : (
                <div className="language-add-cta">
                  <div>
                    <h3>
                      {t("languageNotAdded", {
                        language: languageName(selected, i18n.language),
                      })}
                    </h3>
                    <p className="work-muted">{t("missingSourceHelp")}</p>
                  </div>
                  <Button onClick={() => select(product.base_language)}>
                    {t("addSource")}
                  </Button>
                </div>
              )}
            </Card>
            <aside className="product-rail" aria-label={t("productWide")}>
              <PictogramsCard details={details} />
              <SourceFilePanel
                product={product}
                onChanged={load}
                onExtract={(text, fileId, extractionId, pictograms) => {
                  setExtracted({ text, fileId, extractionId, pictograms });
                  select(product.base_language);
                }}
              />
              {canWebsite ? (
                <WebsitePanel
                  product={product}
                  website={website}
                  setWebsite={setWebsite}
                  startOpen={params.get("website") === "1"}
                />
              ) : (
                <Card aria-label={t("website")}>
                  <CardHeader
                    title={t("website")}
                    hint={t(onWebsite ? "onWebsite" : "notOnWebsite")}
                  />
                  <CardBody />
                </Card>
              )}
            </aside>
          </div>
          <ConfirmDialog
            open={deleteOpen}
            onOpenChange={setDeleteOpen}
            title={t("deleteProduct")}
            description={t("deleteProductWarning", { name: product.name })}
            confirmLabel={t("deleteProduct")}
            pending={busy}
            onConfirm={remove}
          />
        </div>
      )}
    </ProductDetailsScope>
  );
}

function LanguagePicker({
  languages,
  onSelect,
  startOpen,
}: {
  languages: string[];
  onSelect: (language: string) => void;
  startOpen: boolean;
}) {
  const { t, i18n } = useTranslation("work");
  const [open, setOpen] = useState(startOpen);
  const [query, setQuery] = useState("");
  const matches = languages
    .map((code) => ({ code, name: languageName(code, i18n.language) }))
    .filter(({ code, name }) =>
      `${name} ${code}`.toLowerCase().includes(query.trim().toLowerCase()),
    );
  const pick = (code: string) => {
    setOpen(false);
    setQuery("");
    onSelect(code);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="vl-tab language-add">
          <Plus aria-hidden="true" />
          {t("addLanguage")}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="language-picker"
        align="start"
        sideOffset={4}
        aria-label={t("addLanguage")}
      >
        <Input
          autoFocus
          aria-label={t("searchLanguages")}
          placeholder={t("searchLanguages")}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && matches[0]) {
              event.preventDefault();
              pick(matches[0].code);
            }
          }}
        />
        <div className="language-picker-list">
          {matches.map(({ code, name }) => (
            <button
              type="button"
              className="vl-menu-item"
              key={code}
              onClick={() => pick(code)}
            >
              {name}
            </button>
          ))}
          {!matches.length && (
            <p className="work-muted">{t("noLanguageMatches")}</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
